import { Box, Card, Checkbox, Chip, CircularProgress, Grid, Input, Typography } from "@mui/joy";
import { CheckCircle2, Search } from "lucide-react";
import type { ISupplier } from "../../../interfaces";

interface SupplierSelectionGridProps {
    suppliers: ISupplier[];
    selectedSuppliers: ISupplier[];
    isLoading: boolean;
    searchQuery: string;
    onSearchChange: (query: string) => void;
    onToggleSelection: (supplier: ISupplier) => void;
}

export function SupplierSelectionGrid({
    suppliers,
    selectedSuppliers,
    isLoading,
    searchQuery,
    onSearchChange,
    onToggleSelection,
}: SupplierSelectionGridProps) {
    const filteredSuppliers = suppliers.filter((s: ISupplier) =>
        s.Name.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    if (isLoading) {
        return (
            <Box display="flex" alignItems="center" gap={1} py={3}>
                <CircularProgress size="sm" />
                <Typography level="body-sm">Loading suppliers...</Typography>
            </Box>
        );
    }

    return (
        <>
            {suppliers.length > 3 && (
                <Input
                    placeholder="Search suppliers..."
                    value={searchQuery}
                    onChange={(e) => onSearchChange(e.target.value)}
                    startDecorator={<Search size={14} />}
                    sx={{ mb: 1.5 }}
                    size="sm"
                />
            )}

            {filteredSuppliers.length === 0 ? (
                <Box
                    sx={{
                        p: 2,
                        textAlign: "center",
                        borderRadius: "sm",
                        bgcolor: "background.level1",
                        border: "1px dashed",
                        borderColor: "divider",
                    }}
                >
                    <Typography level="body-xs" sx={{ color: "text.secondary" }}>
                        No suppliers found matching "{searchQuery}"
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={1.5}>
                    {filteredSuppliers.map((supplier: ISupplier) => {
                        const isSelected = selectedSuppliers.some((s) => s.Id === supplier.Id);
                        return (
                            <Grid xs={12} sm={6} md={4} key={supplier.Id}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        p: 1.5,
                                        cursor: "pointer",
                                        transition: "all 0.2s ease",
                                        border: "1.5px solid",
                                        borderColor: isSelected ? "primary.500" : "divider",
                                        bgcolor: isSelected ? "primary.softBg" : "background.surface",
                                        "&:hover": {
                                            borderColor: isSelected ? "primary.500" : "primary.400",
                                            bgcolor: isSelected ? "primary.softBg" : "background.level1",
                                            transform: "translateY(-2px)",
                                            boxShadow: "sm",
                                        },
                                    }}
                                    onClick={() => onToggleSelection(supplier)}
                                >
                                    <Box display="flex" alignItems="flex-start" gap={1.5}>
                                        <Checkbox
                                            checked={isSelected}
                                            onChange={() => onToggleSelection(supplier)}
                                            onClick={(e) => e.stopPropagation()}
                                            size="md"
                                            sx={{ flexShrink: 0, mt: 0.25 }}
                                        />
                                        <Box flex={1} minWidth={0}>
                                            <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                                                <Typography level="body-sm" sx={{ fontWeight: 600, flex: 1 }}>
                                                    {supplier.Name}
                                                </Typography>
                                                {isSelected && (
                                                    <CheckCircle2
                                                        size={16}
                                                        style={{
                                                            color: "var(--joy-palette-primary-plainColor)",
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                                <Chip
                                                    size="sm"
                                                    variant="soft"
                                                    color={supplier.Status === "Active" ? "success" : "neutral"}
                                                    sx={{
                                                        fontSize: "0.7rem",
                                                        height: 20,
                                                        px: 0.75,
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {supplier.Status}
                                                </Chip>
                                                {supplier.Type && (
                                                    <Typography
                                                        level="body-xs"
                                                        sx={{
                                                            color: "text.tertiary",
                                                            fontWeight: 400,
                                                        }}
                                                    >
                                                        {supplier.Type}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {selectedSuppliers.length === 0 && (
                <Typography level="body-xs" sx={{ color: "danger.plainColor", mt: 1.5 }}>
                    Please select at least one supplier to compare
                </Typography>
            )}
        </>
    );
}
